/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* v135 — état de variante robuste + UX premium des formats épuisés */
(function () {
  function money(cents) {
    try {
      return new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'EUR'}).format((cents || 0) / 100);
    } catch (e) {
      return '€' + ((cents || 0) / 100).toFixed(2).replace('.', ',');
    }
  }

  function setButtonText(btn, text) {
    if (!btn) return;
    btn.setAttribute('data-button-content', text);
    if (btn.hasAttribute('aria-busy')) return;
    if (btn.firstElementChild && btn.getAttribute('is') === 'loader-button') {
      btn.firstElementChild.innerHTML = text;
    } else {
      btn.innerHTML = text;
    }
  }

  function buttonsForForm(form) {
    if (!form) return [];
    var scope = form.closest('.product-form') || document;
    var buttons = Array.prototype.slice.call(scope.querySelectorAll('[data-product-add-to-cart-button]'));
    document.querySelectorAll('#StickyAddToCart, product-sticky-form [data-product-add-to-cart-button]').forEach(function (btn) {
      if (buttons.indexOf(btn) === -1) buttons.push(btn);
    });
    return buttons;
  }

  function syncProductState(form, variant) {
    if (!form || !variant) return;
    var available = !!variant.available;
    var info = form.closest('[data-exo-product-info]') || document.querySelector('[data-exo-product-info]');
    if (info) info.classList.toggle('is-variant-unavailable', !available);

    buttonsForForm(form).forEach(function (btn) {
      if (!btn) return;
      if (available) {
        btn.removeAttribute('disabled');
        btn.classList.remove('button--ternary');
        btn.classList.add(btn.hasAttribute('data-use-primary') ? 'button--primary' : 'button--secondary');
        var base = btn.getAttribute('data-exo-base') || 'Ajouter au panier';
        setButtonText(btn, base + ' — ' + money(variant.price));
      } else {
        btn.setAttribute('disabled', 'disabled');
        btn.classList.remove('button--primary', 'button--secondary');
        btn.classList.add('button--ternary');
        setButtonText(btn, 'Indisponible');
      }
    });

    var note = (info || document).querySelector('[data-exo-unavailable-note]');
    if (note) note.hidden = available;
  }

  function bindVariants(root) {
    (root || document).querySelectorAll('product-variants').forEach(function (variantsEl) {
      if (variantsEl.dataset.exoV135Bound) return;
      variantsEl.dataset.exoV135Bound = '1';

      var formId = variantsEl.getAttribute('form-id');
      var form = formId ? document.getElementById(formId) : null;

      /* Focal marque les variantes épuisées "is-disabled", mais elles doivent rester
         consultables pour afficher clairement leur prix et l'état Indisponible. */
      variantsEl.addEventListener('click', function (event) {
        var label = event.target.closest('.block-swatch__item, .color-swatch__item, .variant-swatch__item');
        if (!label) return;
        var id = label.getAttribute('for');
        if (!id) return;
        var input = variantsEl.querySelector('#' + CSS.escape(id));
        if (!input || input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', {bubbles: true}));
      });

      if (form && !form.dataset.exoV135Bound) {
        form.dataset.exoV135Bound = '1';
        form.addEventListener('variant:changed', function (event) {
          var variant = event.detail && event.detail.variant;
          if (variant) syncProductState(form, variant);
        });
      }

      /* Le JSON produit est chargé de façon asynchrone par le composant Focal.
         On attend brièvement la variante sélectionnée pour synchroniser l'état initial. */
      var attempts = 0;
      var initialSync = window.setInterval(function () {
        attempts += 1;
        var variant = variantsEl.selectedVariant;
        if (variant && form) {
          syncProductState(form, variant);
          window.clearInterval(initialSync);
        } else if (attempts > 30) {
          window.clearInterval(initialSync);
        }
      }, 100);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bindVariants(document); });
  } else {
    bindVariants(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    bindVariants(event.target || document);
  });
})();
